import { useCallback, useRef, useState } from 'react';
import { Platform } from 'react-native';
import type { TimeWindow } from '../utils/calendarDayGeometry';
import {
  pixelDeltaToMinutes,
  resolveDraggedSpan,
  resolveResizedSpan,
} from '../utils/calendarDragSnap';

interface UseEventDragOptions {
  /** The event's current span, in minutes since local midnight. */
  span: TimeWindow;
  /** The slice of the day the grid is drawing. */
  window: TimeWindow;
  gridHeight: number;
  /** Called once, on release, only when the span actually changed. */
  onCommit: (span: TimeWindow) => void;
  /** Off for events that must not move — a cancelled meeting, an all-day block. */
  enabled?: boolean;
}

interface UseEventDragReturn {
  canDrag: boolean;
  isDragging: boolean;
  /** Where the block should be drawn mid-gesture; null when not dragging. */
  previewSpan: TimeWindow | null;
  onDragStart: (y: number) => void;
  onResizeStart: (y: number) => void;
  onDragMove: (y: number) => void;
  onDragEnd: () => void;
  onDragCancel: () => void;
}

type Mode = 'move' | 'resize';

/**
 * Dragging an event to a new time. DESKTOP ONLY.
 *
 * On a phone the equivalent gesture would have to be a long-press, and
 * `useEventPreview` already owns that (350ms) as the preview's ONLY native
 * trigger — taking it would delete the mobile preview to add a gesture that a
 * 44px block inside a scrolling grid handles badly anyway. Tap already opens
 * the edit sheet on mobile, so nothing is lost there.
 *
 * Deliberately a plain state machine over y-pixels rather than a gesture
 * library: every decision it makes is driven and asserted in tests, and the
 * web mouse binding on top is a few lines with no logic of its own.
 *
 * Platform.OS is compared directly, not via Platform.select — the latter is
 * not reliably re-evaluated when a test mutates Platform.OS under this
 * project's jest-expo/web setup (same reason as useEventPreview).
 */
export function useEventDrag({
  span,
  window,
  gridHeight,
  onCommit,
  enabled = true,
}: UseEventDragOptions): UseEventDragReturn {
  const canDrag = Platform.OS === 'web' && enabled;

  const [previewSpan, setPreviewSpan] = useState<TimeWindow | null>(null);
  const origin = useRef<{ y: number; mode: Mode } | null>(null);

  const begin = useCallback(
    (y: number, mode: Mode) => {
      if (!canDrag) return;
      origin.current = { y, mode };
    },
    [canDrag],
  );

  const onDragStart = useCallback((y: number) => begin(y, 'move'), [begin]);
  const onResizeStart = useCallback((y: number) => begin(y, 'resize'), [begin]);

  const onDragMove = useCallback(
    (y: number) => {
      const started = origin.current;
      if (!started) return;

      const deltaMinutes = pixelDeltaToMinutes(y - started.y, window, gridHeight);
      setPreviewSpan(
        started.mode === 'resize'
          ? resolveResizedSpan(span, deltaMinutes, window)
          : resolveDraggedSpan(span, deltaMinutes, window),
      );
    },
    [span, window, gridHeight],
  );

  const reset = useCallback(() => {
    origin.current = null;
    setPreviewSpan(null);
  }, []);

  const onDragEnd = useCallback(() => {
    const moved = previewSpan;
    reset();

    // A click is a drag of zero pixels. Committing there would rewrite the
    // event with its own times on every tap — a round trip that buys nothing
    // and can still fail the slot check and bounce the block.
    if (!moved) return;
    if (moved.startMinutes === span.startMinutes && moved.endMinutes === span.endMinutes) return;

    onCommit(moved);
  }, [previewSpan, reset, span, onCommit]);

  return {
    canDrag,
    isDragging: previewSpan !== null,
    previewSpan,
    onDragStart,
    onResizeStart,
    onDragMove,
    onDragEnd,
    onDragCancel: reset,
  };
}
